import { clsx } from 'clsx';

import { Badge } from '@/vibes/soul/primitives/badge';
import { Price, PriceLabel } from '@/vibes/soul/primitives/price-label';
import * as Skeleton from '@/vibes/soul/primitives/skeleton';
import { Image } from '~/components/image';
import { Link } from '~/components/link';
import { placeholderImageUrl } from '~/lib/store-assets';

import { Rating } from '../rating';

import { AddToCartForm } from './add-to-cart-form';
import { Compare } from './compare';
import { WishlistButton } from '../wishlist-button';

export interface Product {
  id: string;
  title: string;
  href: string;
  image?: { src: string; alt: string };
  price?: Price;
  subtitle?: string;
  badge?: string;
  rating?: number;
  isInStock?: boolean;
  inventoryMessage?: string;
  numberOfReviews?: number;
  isInWishlist?: boolean;
  showWishlist?: boolean;
  isLoggedIn?: boolean;
}

export interface ProductCardProps {
  className?: string;
  colorScheme?: 'light' | 'dark';
  aspectRatio?: '5:6' | '3:4' | '1:1';
  showCompare?: boolean;
  showAddToCart?: boolean;
  imagePriority?: boolean;
  imageSizes?: string;
  compareLabel?: string;
  compareParamName?: string;
  addToCartLabel?: string;
  product: Product;
  showRating?: boolean;
}

// eslint-disable-next-line valid-jsdoc
/**
 * This component supports various CSS variables for theming. Here's a comprehensive list, along
 * with their default values:
 *
 * ```css
 * :root {
 *   --product-card-focus: hsl(var(--primary));
 *   --product-card-light-offset: hsl(var(--background));
 *   --product-card-light-background: hsl(var(--contrast-100));
 *   --product-card-light-title: hsl(var(--foreground));
 *   --product-card-light-subtitle: hsl(var(--foreground) / 75%);
 *   --product-card-light-message: hsl(var(--foreground) / 75%);
 *   --product-card-dark-offset: hsl(var(--foreground));
 *   --product-card-dark-background: hsl(var(--contrast-500));
 *   --product-card-dark-title: hsl(var(--background));
 *   --product-card-dark-subtitle: hsl(var(--background) / 75%);
 *   --product-card-dark-message: hsl(var(--background) / 75%);
 *   --product-card-font-family: var(--font-family-body);
 * }
 * ```
 */
export function ProductCard({
  product: {
    id,
    title,
    subtitle,
    badge,
    price,
    image,
    href,
    isInStock,
    inventoryMessage,
    rating,
    numberOfReviews,
    isInWishlist,
    showWishlist,
    isLoggedIn,
  },
  showRating = false,
  colorScheme = 'light',
  className,
  showCompare = false,
  showAddToCart = false,
  aspectRatio = '1:1',
  compareLabel,
  compareParamName,
  addToCartLabel,
  imagePriority = false,
  imageSizes = '(min-width: 80rem) 20vw, (min-width: 64rem) 25vw, (min-width: 42rem) 33vw, (min-width: 24rem) 50vw, 100vw',
}: ProductCardProps) {
  return (
    <article
      className={clsx(
        'group flex min-w-0 max-w-md justify-between flex-col gap-2 font-[family-name:var(--card-font-family,var(--font-family-body))] @container',
        className,
      )}
    >
      <div className="relative">
        <div
          className={clsx(
            'relative overflow-hidden',
            {
              '5:6': 'aspect-[5/6]',
              '3:4': 'aspect-[3/4]',
              '1:1': 'aspect-square',
            }[aspectRatio],
            {
              light: 'bg-[var(--product-card-light-background,hsl(var(--contrast-100)))]',
              dark: 'bg-[var(--product-card-dark-background,hsl(var(--contrast-500)))]',
            }[colorScheme],
          )}
        >
          {image != null ? (
            <Image
              alt={image.alt}
              className={clsx(
                'w-full scale-100 select-none object-cover transition-transform duration-500 ease-out group-hover:scale-110',
                {
                  light: 'bg-[var(--product-card-light-background,hsl(var(--contrast-100))]',
                  dark: 'bg-[var(--product-card-dark-background,hsl(var(--contrast-500))]',
                }[colorScheme],
              )}
              fill
              preload={imagePriority}
              sizes={imageSizes}
              src={image.src}
            />
          ) : (
            <Image
              alt={`${title} - No image available`}
              className={clsx(
                'w-full scale-100 select-none object-cover transition-transform duration-500 ease-out group-hover:scale-110',
                {
                  light: 'bg-[var(--product-card-light-background,hsl(var(--contrast-100))]',
                  dark: 'bg-[var(--product-card-dark-background,hsl(var(--contrast-500))]',
                }[colorScheme],
              )}
              fill
              priority={imagePriority}
              sizes={imageSizes}
              src={placeholderImageUrl()}
            />
          )}
          {badge != null && badge !== '' && (
            <Badge className="absolute left-3 top-3" shape="rounded">
              {badge}
            </Badge>
          )}
        </div>

        <div className="mt-2 flex flex-col items-start gap-x-4 gap-y-3 px-1 @xs:mt-3 @2xl:flex-row">
          <div className="flex-1 text-sm @[16rem]:text-base">
          {subtitle != null && subtitle !== '' && (
              <span
                  className={clsx(
                      'block text-sm font-normal uppercase mb-1',
                      {
                          light: 'text-[var(--product-card-light-subtitle,hsl(var(--contrast-600)))]',
                          dark: 'text-[var(--product-card-dark-subtitle,hsl(var(--background)/75%))]',
                      }[colorScheme],
                  )}
                  >
                {subtitle}
              </span>
          )}
            <span
              className={clsx(
                'block font-semibold mb-4',
                {
                  light: 'text-[var(--product-card-light-title,hsl(var(--foreground)))]',
                  dark: 'text-[var(--product-card-dark-title,hsl(var(--background)))]',
                }[colorScheme],
              )}
            >
              {title}
            </span>
              {showRating && typeof rating === 'number' && rating > 0 && (
                  <div className="mb-4 mt-2">
                    <Rating numberOfReviews={numberOfReviews} rating={rating} />
                  </div>
              )}
            {price != null && <PriceLabel colorScheme={colorScheme} price={price} className='font-normal mb-4' />}

            <span
              className={clsx(
                'block text-sm font-normal',
                {
                  light: 'text-[var(--product-card-light-message,hsl(var(--foreground)/75%))]',
                  dark: 'text-[var(--product-card-dark-message,hsl(var(--background)/75%))]',
                }[colorScheme],
              )}
            >
              {inventoryMessage}
            </span>
          </div>
        </div>
        {href !== '#' && (
          <Link
            aria-label={title}
            className={clsx(
              'absolute inset-0 rounded-b-lg rounded-t-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--product-card-focus,hsl(var(--primary)))] focus-visible:ring-offset-4',
              {
                light: 'ring-offset-[var(--product-card-light-offset,hsl(var(--background)))]',
                dark: 'ring-offset-[var(--product-card-dark-offset,hsl(var(--foreground)))]',
              }[colorScheme],
            )}
            href={href}
            id={id}
          >
            <span className="sr-only">View product</span>
          </Link>
        )}
        <WishlistButton
          productId={Number(id)}
          isInWishlist={isInWishlist}
          showWishlist={showWishlist}
          isLoggedIn={isLoggedIn}
        />
      </div>
      {showCompare && (
        <div className="mt-0.5 shrink-0">
          <Compare
            colorScheme={colorScheme}
            label={compareLabel}
            paramName={compareParamName}
            product={{ id, title, href, image }}
          />
        </div>
      )}
      {showAddToCart && (
        <div className="mt-2 shrink-0">
          <AddToCartForm
            productId={id}
            productHref={href}
            addToCartLabel={addToCartLabel}
            isInStock={isInStock}
            inventoryMessage={inventoryMessage}
          />
        </div>
      )}
    </article>
  );
}

export function ProductCardSkeleton({
  className,
  aspectRatio = '1:1',
}: Pick<ProductCardProps, 'className' | 'aspectRatio'>) {
  return (
    <Skeleton.Root className={clsx(className)}>
      <Skeleton.Box
        className={clsx(
          'rounded-[var(--product-card-border-radius,1rem)]',
          {
            '5:6': 'aspect-[5/6]',
            '3:4': 'aspect-[3/4]',
            '1:1': 'aspect-square',
          }[aspectRatio],
        )}
      />
      <div className="mt-2 flex flex-col items-start gap-x-4 gap-y-3 px-1 @xs:mt-3 @2xl:flex-row">
        <div className="w-full text-sm @[16rem]:text-base">
          <Skeleton.Text characterCount={10} />
          <Skeleton.Text characterCount={8} />
          <Skeleton.Text characterCount={6} />
        </div>
      </div>
    </Skeleton.Root>
  );
}
